import { Checkbox, CheckboxGroup, Radio, RadioGroup } from '@main/complykit';
import {
  dynamicFieldName,
  DynamicFormFieldComponent,
  OptionsFieldConfig,
  OptionsFieldOption,
} from '@main/dynamic-form';
import { Controller, useFormContext } from 'react-hook-form';

import { NotAnswered } from './not-answered';

export const OptionsField: DynamicFormFieldComponent<OptionsFieldConfig> = ({
  config,
  state,
  labelId,
}) => {
  const { control } = useFormContext();

  return (
    <Controller
      name={config.name}
      control={control}
      rules={config.validation}
      render={({ field }) => {
        const value: unknown = field.value;

        if (state.isReadOnly && (!value || (Array.isArray(value) && !value.length))) {
          return <NotAnswered />;
        }

        const shared = {
          ...dynamicFieldName({ config, labelId }),
          ref: field.ref,
          name: field.name,
          onBlur: field.onBlur,
          isRequired: state.isRequired,
          isDisabled: state.isDisabled,
          isReadOnly: state.isReadOnly,
          error: state.error,
        };

        return config.isMultiOptions ? (
          <CheckboxGroup
            {...shared}
            value={(field.value ?? []) as string[]}
            onChange={field.onChange}
          >
            {config.options?.map((option) => (
              <Checkbox key={option.value} value={option.value}>
                {option.label}
              </Checkbox>
            ))}
          </CheckboxGroup>
        ) : (
          <RadioGroup
            {...shared}
            value={(field.value as string | undefined) ?? null}
            onChange={field.onChange}
          >
            {config.options?.map((option) => (
              <Radio key={option.value} value={option.value}>
                {option.label}
              </Radio>
            ))}
          </RadioGroup>
        );
      }}
    />
  );
};

OptionsField.resolveReadableValues = ({ config }) => {
  if (!config.value || !config.options?.length) {
    return [];
  }

  const valueToLabel = mapValueToLabel(config.options);

  if (Array.isArray(config.value)) {
    return config.value.map(valueToLabel);
  }

  return [valueToLabel(config.value)];
};

function mapValueToLabel(options: OptionsFieldOption[]) {
  return (value: string) => options.find((option) => option.value === value)?.label ?? value;
}
